import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Search, ChevronDown, User, ArrowRight } from "lucide-react";
import { CATEGORIES, CATEGORY_LABELS, SUBCATEGORIES, SUBCATEGORY_LABELS } from "../lib/constants";
import { useBrand } from "../context/BrandContext";
import { useAuth } from "../context/AuthContext";

// Two hard-ruled decks: a masthead carrying the lime logo slab, and a strip of
// bordered nav boxes beneath it. Every control is a box with a black edge and
// an offset shadow that presses in on hover.
const BOX =
    "border-2 border-foreground bg-surface shadow-inset hover:bg-accent hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-none transition-[transform,box-shadow,background-color] duration-150";

export function Navbar() {
    const brand = useBrand();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [q, setQ] = useState("");

    const doSearch = (e) => {
        e.preventDefault();
        if (q.trim()) {
            navigate(`/search?q=${encodeURIComponent(q.trim())}`);
            setSearchOpen(false);
            setMobileOpen(false);
            setQ("");
        }
    };

    return (
        <header className="sticky top-0 z-50 glass border-b-[3px] border-foreground" data-testid="navbar">
            <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-[78px] gap-4">
                    <button
                        className={`lg:hidden -ml-1 p-2 text-foreground ${BOX}`}
                        onClick={() => setMobileOpen((v) => !v)}
                        aria-label="Toggle menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>

                    {/* Logo slab — solid lime, black edge, hard shadow */}
                    <Link
                        to="/"
                        className="shrink-0 border-[3px] border-foreground bg-accent px-3 py-2 shadow-card hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-inset transition-[transform,box-shadow] duration-150"
                        data-testid="navbar-logo"
                    >
                        <span className="font-display text-lg sm:text-2xl uppercase leading-none text-accent-foreground">
                            {brand.site_name}
                        </span>
                    </Link>

                    <div className="flex items-center gap-2 sm:gap-3">
                        <button
                            onClick={() => setSearchOpen((v) => !v)}
                            className={`p-2 text-foreground ${BOX}`}
                            aria-label="Search"
                            data-testid="navbar-search-toggle"
                        >
                            <Search size={18} />
                        </button>
                        {user ? (
                            <div className="hidden sm:flex items-center gap-3">
                                <Link
                                    to="/account"
                                    data-testid="navbar-account"
                                    className={`flex items-center gap-2 px-3 py-2 eyebrow text-foreground ${BOX}`}
                                >
                                    <User size={13} />
                                    {user.name?.split(" ")[0]}
                                </Link>
                                <button
                                    onClick={() => { logout(); navigate("/"); }}
                                    data-testid="navbar-logout"
                                    className={`px-3 py-2 eyebrow text-foreground ${BOX}`}
                                >
                                    Log out
                                </button>
                            </div>
                        ) : (
                            <div className="hidden sm:flex items-center gap-3">
                                <Link
                                    to="/login"
                                    data-testid="navbar-login"
                                    className={`px-3 py-2 eyebrow text-foreground ${BOX}`}
                                >
                                    Log in
                                </Link>
                                <Link
                                    to="/register"
                                    data-testid="navbar-register"
                                    className="border-[3px] border-foreground bg-accent px-5 py-2.5 eyebrow text-accent-foreground shadow-card hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-inset transition-[transform,box-shadow] duration-150"
                                >
                                    Subscribe
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Nav deck — bordered boxes on a grey strip */}
            <nav className="hidden lg:block border-t-[3px] border-foreground bg-surface-raised">
                <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
                    <ul className="flex items-center gap-2.5 py-3">
                        {CATEGORIES.map((cat) => (
                            <li key={cat} className="relative group">
                                <Link
                                    to={`/category/${cat}`}
                                    data-testid={`nav-cat-${cat}`}
                                    className={`flex items-center gap-1.5 px-3 py-2 eyebrow text-foreground ${BOX}`}
                                >
                                    {CATEGORY_LABELS[cat]}
                                    {cat === "personal-finance" && <ChevronDown size={12} strokeWidth={3} />}
                                </Link>
                                {cat === "personal-finance" && (
                                    <div className="absolute left-0 top-full pt-3 w-60 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-150 z-50">
                                        <div className="border-[3px] border-foreground bg-surface shadow-lift">
                                            {SUBCATEGORIES.map((sub) => (
                                                <Link
                                                    key={sub}
                                                    to={`/category/personal-finance/${sub}`}
                                                    data-testid={`nav-sub-${sub}`}
                                                    className="group/sub flex items-center justify-between px-4 py-3 eyebrow text-foreground border-b-2 border-foreground last:border-b-0 hover:bg-accent transition-colors duration-150"
                                                >
                                                    {SUBCATEGORY_LABELS[sub]}
                                                    <ArrowRight size={13} strokeWidth={3} className="opacity-0 transition-opacity duration-150 group-hover/sub:opacity-100" />
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </li>
                        ))}
                        <li className="ml-auto">
                            <Link
                                to="/archive"
                                data-testid="nav-archive"
                                className={`block px-3 py-2 eyebrow text-foreground ${BOX}`}
                            >
                                Archive
                            </Link>
                        </li>
                    </ul>
                </div>
            </nav>

            {/* Search panel */}
            {searchOpen && (
                <div className="border-t-[3px] border-foreground bg-accent-soft" data-testid="navbar-search-panel">
                    <form onSubmit={doSearch} className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-5 flex gap-3">
                        <input
                            autoFocus
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search articles, markets, authors..."
                            data-testid="navbar-search-input"
                            className="field"
                        />
                        <button
                            type="submit"
                            data-testid="navbar-search-submit"
                            className="shrink-0 border-[3px] border-foreground bg-foreground px-8 eyebrow text-accent shadow-card hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-inset transition-[transform,box-shadow] duration-150"
                        >
                            Search
                        </button>
                    </form>
                </div>
            )}

            {/* Mobile menu */}
            {mobileOpen && (
                <nav className="lg:hidden border-t-[3px] border-foreground bg-surface" data-testid="mobile-menu">
                    <ul>
                        {CATEGORIES.map((cat) => (
                            <li key={cat} className="border-b-2 border-foreground">
                                <Link
                                    to={`/category/${cat}`}
                                    onClick={() => setMobileOpen(false)}
                                    className="flex items-center justify-between px-5 py-3.5 eyebrow text-foreground hover:bg-accent transition-colors duration-150"
                                >
                                    {CATEGORY_LABELS[cat]}
                                    <ArrowRight size={14} strokeWidth={3} />
                                </Link>
                            </li>
                        ))}
                        <li className="border-b-2 border-foreground">
                            <Link
                                to="/archive"
                                onClick={() => setMobileOpen(false)}
                                className="flex items-center justify-between px-5 py-3.5 eyebrow text-foreground hover:bg-accent transition-colors duration-150"
                            >
                                Archive
                                <ArrowRight size={14} strokeWidth={3} />
                            </Link>
                        </li>
                        {!user && (
                            <li className="flex gap-3 p-4">
                                <Link to="/login" onClick={() => setMobileOpen(false)} className="flex-1 text-center border-2 border-foreground bg-surface py-3 eyebrow text-foreground shadow-inset">
                                    Log in
                                </Link>
                                <Link to="/register" onClick={() => setMobileOpen(false)} className="flex-1 text-center border-2 border-foreground bg-accent py-3 eyebrow text-accent-foreground shadow-inset">
                                    Subscribe
                                </Link>
                            </li>
                        )}
                        {user && (
                            <li className="p-4 space-y-3">
                                <Link to="/account" onClick={() => setMobileOpen(false)} data-testid="mobile-account" className="block w-full text-center border-2 border-foreground bg-surface py-3 eyebrow text-foreground shadow-inset">
                                    My account
                                </Link>
                                <button onClick={() => { logout(); setMobileOpen(false); navigate("/"); }} className="w-full border-2 border-foreground bg-neg py-3 eyebrow text-foreground shadow-inset">
                                    Log out
                                </button>
                            </li>
                        )}
                    </ul>
                </nav>
            )}
        </header>
    );
}
